/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useEffect, useState } from "react";

type ScreenOrientation = "portrait" | "landscape";

const getPortraitMediaQuery = () =>
  typeof window !== "undefined"
    ? window.matchMedia("screen and (orientation: portrait)")
    : undefined;

const getViewportSize = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const viewport = window.visualViewport;
  const width =
    viewport?.width ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  const height =
    viewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight;

  return width && height ? { width, height } : undefined;
};

const getScreenOrientationFromMediaQuery = ():
  | ScreenOrientation
  | undefined => {
  const portraitMediaQuery = getPortraitMediaQuery();

  if (!portraitMediaQuery) {
    return undefined;
  }

  return portraitMediaQuery.matches ? "portrait" : "landscape";
};

const getScreenOrientationFromViewport = ():
  | ScreenOrientation
  | undefined => {
  const viewportSize = getViewportSize();

  if (!viewportSize) {
    return undefined;
  }

  return viewportSize.width <= viewportSize.height ? "portrait" : "landscape";
};

const getScreenOrientation = (): ScreenOrientation => {
  return (
    getScreenOrientationFromMediaQuery() ??
    getScreenOrientationFromViewport() ??
    "landscape"
  );
};

export const useScreenOrientation = (): ScreenOrientation => {
  const [orientation, setOrientation] =
    useState<ScreenOrientation>(getScreenOrientation);

  useEffect(() => {
    const mediaQuery = getPortraitMediaQuery();

    const callback = () => {
      setOrientation(getScreenOrientation());
    };

    window.addEventListener("resize", callback);
    window.addEventListener("orientationchange", callback);
    window.visualViewport?.addEventListener("resize", callback);
    mediaQuery?.addEventListener("change", callback);

    return () => {
      window.removeEventListener("resize", callback);
      window.removeEventListener("orientationchange", callback);
      window.visualViewport?.removeEventListener("resize", callback);
      mediaQuery?.removeEventListener("change", callback);
    };
  }, []);

  return orientation;
};
